import { MaxColumnsContainer } from '@/components/ui/layout';
import { EnumButtonName, EnumDialogName } from '@calm/evexia-analytics';
import {
    DS_BREAKPOINT,
    DS_FONT_SIZE,
    DS_HEIGHT,
} from '@calm/evexia-design-tokens';
import UpgradeDialogBackdrop from '@rhg/../public/_n/images/upgrade-dialog-backdrop.png';
import {
    BOTTOM_ACTION_SECTION_HEIGHT_DESKTOP,
    BOTTOM_ACTION_SECTION_HEIGHT_MOBILE,
    BottomAction,
} from '@rhg/components/bottom-action';
import { DialogCloseButton } from '@rhg/components/dialog-close-button';
import { Box, Dialog, Stack, Theme, Typography } from '@rhg/libs/material';
import {
    NewContent,
    PositiveRelationships,
    Stars,
} from '@rhg/libs/material/icons';
import {
    MaskOrientation,
    styleMaskedContainer,
    sxAppend,
} from '@rhg/libs/material/sx';
import { resolveColor } from '@rhg/libs/material/theme/palette';
import NextImage from '@rhg/libs/next/image';
import { displayLocaleDateString } from '@rhg/utils/date-time';
import { useIntl } from 'react-intl';
import { upgradeDialogMessages } from './messages';

export interface UpgradeDialogProps {
    isOpen: boolean;
    deadlineDate?: Date;
    isPastDeadline?: boolean;
    onUpgrade: () => void;
    onDismiss: () => void;
}

const HEADLINE_ID = 'upgrade-dialog-headline';
const CONTENT_MAX_WIDTH_COLUMNS = 6;
const BOTTOM_ACTION_SPACER_HEIGHT = {
    xs: BOTTOM_ACTION_SECTION_HEIGHT_MOBILE,
    sm: BOTTOM_ACTION_SECTION_HEIGHT_MOBILE,
    md: BOTTOM_ACTION_SECTION_HEIGHT_DESKTOP,
    lg: BOTTOM_ACTION_SECTION_HEIGHT_DESKTOP,
};

const HERO_FIXED_HEIGHT_PX = 420;
const HERO_MOBILE_HEIGHT_PX = 250;

const HERO_FIXED_IMAGE_BLEED = 200;
const HERO_FIXED_IMAGE_WIDTH = 1924;
const HERO_MOBILE_IMAGE_WIDTH = 1145;

const heroMobileMediaSelector = `@media(max-width: ${DS_BREAKPOINT.MEDIUM}px)`;
const heroFixedMediaSelector = `@media(min-width: ${
    DS_BREAKPOINT.MEDIUM
}px) and (max-width: ${HERO_FIXED_IMAGE_WIDTH - HERO_FIXED_IMAGE_BLEED}px)`;
const heroUltraMediaSelector = `@media(min-width: ${
    HERO_FIXED_IMAGE_WIDTH - HERO_FIXED_IMAGE_BLEED
}px)`;

const styleContentContainer = {
    pt: (theme: Theme) => theme.ds.spacingUnits.SECTION,
    pb: 8,
    px: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.SIDE_SMALL,
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_SMALL,
        lg: 0,
    }),
    textAlign: 'center' as const,
};

const styleBenefitList = {
    listStyle: 'none',
    p: 0,
    m: 0,
};

const styleBenefitRow = {
    textAlign: 'left' as const,
};

// Matches the theme's MuiIconButton `sizeSmall` box/icon tokens (32px
// box, 24px icon) — this is a decorative container, not an IconButton
// itself, so it borrows the same size tokens rather than the component.
//
// With the row's alignItems: flex-start, this box's height (one h6 line)
// top-aligns it to the text's first line on multi-line rows, and matches
// the text's own height on the single-line row.
const styleBenefitIconContainer = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: DS_HEIGHT.ICON_BUTTON_SMALL,
    height: DS_HEIGHT.ICON_BUTTON_SMALL,
    fontSize: DS_FONT_SIZE.ICON_BUTTON_SMALL,
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'common.white' : 'info.dark',
    color: 'info.main',
};

const styleDeadlinePod = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'common.white' : 'mask.black.20p',
    borderRadius: 3,
    p: 6,
};

// body1Demi has no entry in MUI's default Typography variantMapping, so it
// renders as an inline <span> — without this, the label and date sit on the
// same line instead of stacking.
const styleDeadlineLine = {
    display: 'block',
};

const styleHeroContainer = {
    width: '100%',
    // Dialog's Paper is a flex column with a fixed viewport height —
    // without this, Paper squeezes this Stack (its direct flex child)
    // instead of scrolling on short viewports. The inner Box's own
    // flexShrink only protects it from the Stack, not from Paper.
    flexShrink: 0,
};

// Width is 1145px under 600px, 1924px until the width reaches 1924px then 100% of the viewport after that,
// to fit the image properly and not show too much of the placeholder elements in this image.
const styleHeroImageBox = {
    position: 'relative' as const,
    [heroMobileMediaSelector]: {
        width: HERO_MOBILE_IMAGE_WIDTH,
    },
    [heroFixedMediaSelector]: {
        width: HERO_FIXED_IMAGE_WIDTH,
    },
    [heroUltraMediaSelector]: {
        width: `calc(100% + ${HERO_FIXED_IMAGE_BLEED}px)`,
    },
    height: { xs: HERO_MOBILE_HEIGHT_PX, md: HERO_FIXED_HEIGHT_PX },
    // To prevent the surrounding Paper element from squeezing this element
    flexShrink: 0,
    overflow: 'hidden',
    pointerEvents: 'none' as const,
};

const styleCloseButton = {
    position: 'fixed' as const,
    // Same right-offset tokens learn-why-dialog's styleCloseButton uses —
    // no shared close-button-position style exists to import (every
    // fullscreen dialog in this codebase defines its own), so this at
    // least matches that precedent's tokens instead of new magic numbers.
    right: (theme: Theme) => ({
        lg: theme.semantics.Spacing[32],
        md: theme.semantics.Spacing[24],
        sm: theme.semantics.Spacing[16],
        xs: theme.semantics.Spacing[16],
    }),
    top: (theme: Theme) => ({
        lg: theme.semantics.Spacing[24],
        md: theme.semantics.Spacing[24],
        sm: theme.semantics.Spacing[40],
        xs: theme.semantics.Spacing[40],
    }),
    borderRadius: '50%',
    // Stays the light-mode white-on-blue treatment in both themes — it sits
    // on the hero photo, not the dialog background, so it doesn't switch
    // with theme.palette.mode the way the rest of the dialog does.
    backgroundColor: 'mask.white.80p',
    color: resolveColor('interactive/primary', 'light'),
    zIndex: 1,
};

const styleDialogBackground = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

const styleBottomActionSpacer = {
    height: BOTTOM_ACTION_SPACER_HEIGHT,
    // Same fix as the hero container above: an empty Box's content size
    // is 0, so the flex column shrinks it before the sibling with real
    // content, collapsing this reserved gap to nothing.
    flexShrink: 0,
};

const styleCtaBarGradient = {
    // BottomAction's own default `backgroundColor` (opaque: paper in light
    // mode, background.primary in dark) stays in place underneath this —
    // only `backgroundImage`, not the `background` shorthand, is set here,
    // so it layers on top instead of resetting that opaque base to
    // transparent. In dark mode the gradient's own stops are already fully
    // opaque and cover the box edge-to-edge, so the base is moot there; in
    // light mode the gradient's alpha-white stops are transparent by
    // design, and it's the opaque base showing through them that keeps the
    // bar from revealing the content scrolling behind it.
    backgroundImage: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0) 100%)'
            : `linear-gradient(90deg, ${theme.palette.background.primaryLight} 0%, ${theme.palette.background.primary} 50%, ${theme.palette.background.primaryLight} 100%)`,
    borderTop: 'none',
};

function formatDeadlineDate(deadlineDate: Date): string | null {
    if (Number.isNaN(deadlineDate.getTime())) {
        return null;
    }

    return displayLocaleDateString(deadlineDate);
}

export default function UpgradeDialog({
    isOpen,
    deadlineDate,
    isPastDeadline = false,
    onUpgrade,
    onDismiss,
}: UpgradeDialogProps) {
    const { formatMessage } = useIntl();
    const formattedDeadlineDate = deadlineDate
        ? formatDeadlineDate(deadlineDate)
        : null;

    return (
        <Dialog
            trackDialog={EnumDialogName.UpgradeTakeover}
            aria-labelledby={HEADLINE_ID}
            open={isOpen}
            onClose={onDismiss}
            fullScreen
            PaperProps={{ sx: styleDialogBackground }}
        >
            <Stack
                sx={styleHeroContainer}
                justifyContent="center"
                alignItems="center"
            >
                <Box
                    aria-hidden
                    sx={sxAppend(
                        styleHeroImageBox,
                        // MaskOrientation.Top fades the BOTTOM edge of this box (the
                        // gradient's transparent stop lands at the bottom for this
                        // orientation) — do not "correct" this back to
                        // MaskOrientation.Bottom, which would invert it.
                        styleMaskedContainer(MaskOrientation.Top, 0.35)
                    )}
                >
                    <NextImage
                        src={UpgradeDialogBackdrop}
                        alt=""
                        fill
                        style={{ objectFit: 'cover' }}
                    />
                </Box>
            </Stack>
            <DialogCloseButton
                trackButton={EnumButtonName.DialogClose}
                isFullScreen
                onClick={onDismiss}
                sx={styleCloseButton}
            />
            <MaxColumnsContainer
                columns={CONTENT_MAX_WIDTH_COLUMNS}
                gap={8}
                sx={styleContentContainer}
            >
                <Typography id={HEADLINE_ID} variant="h4" component="h1">
                    {formatMessage(
                        isPastDeadline
                            ? upgradeDialogMessages.headlinePastDeadline
                            : upgradeDialogMessages.headline
                    )}
                </Typography>

                {!isPastDeadline && formattedDeadlineDate !== null && (
                    <Box sx={styleDeadlinePod}>
                        <Typography
                            variant="body1Demi"
                            color="text.body"
                            sx={styleDeadlineLine}
                        >
                            {formatMessage(upgradeDialogMessages.deadline)}
                        </Typography>
                        <Typography
                            variant="h6"
                            component="span"
                            color="text.sub"
                            sx={styleDeadlineLine}
                        >
                            {formatMessage(upgradeDialogMessages.deadlineDate, {
                                date: formattedDeadlineDate,
                            })}
                        </Typography>
                    </Box>
                )}

                <Stack component="ul" gap={8} sx={styleBenefitList}>
                    <Stack
                        component="li"
                        direction="row"
                        alignItems="flex-start"
                        gap={4}
                        sx={styleBenefitRow}
                    >
                        <Box sx={styleBenefitIconContainer}>
                            <NewContent color="inherit" />
                        </Box>
                        <Typography variant="h6" component="span">
                            {formatMessage(
                                upgradeDialogMessages.benefitContent
                            )}
                        </Typography>
                    </Stack>
                    <Stack
                        component="li"
                        direction="row"
                        alignItems="flex-start"
                        gap={4}
                        sx={styleBenefitRow}
                    >
                        <Box sx={styleBenefitIconContainer}>
                            <PositiveRelationships color="inherit" />
                        </Box>
                        <Typography variant="h6" component="span">
                            {formatMessage(
                                upgradeDialogMessages.benefitFriends
                            )}
                        </Typography>
                    </Stack>
                    <Stack
                        component="li"
                        direction="row"
                        alignItems="flex-start"
                        gap={4}
                        sx={styleBenefitRow}
                    >
                        <Box sx={styleBenefitIconContainer}>
                            <Stars color="inherit" />
                        </Box>
                        <Typography variant="h6" component="span">
                            {formatMessage(upgradeDialogMessages.benefitAll)}
                        </Typography>
                    </Stack>
                </Stack>
            </MaxColumnsContainer>
            <Box sx={styleBottomActionSpacer} />
            <BottomAction
                trackButton={EnumButtonName.UpgradeTakeoverCta}
                label={formatMessage(upgradeDialogMessages.cta)}
                variant="contained"
                onClick={onUpgrade}
                barSx={styleCtaBarGradient}
            />
        </Dialog>
    );
}
